package pl.nickers.match3.ecs

internal fun collectLaserTargets(
    world: World,
    width: Int,
    height: Int,
    origin: GridPositionComponent,
    direction: LaserDirection,
): Set<Int> {
    val targets = mutableSetOf<Int>()
    when (direction) {
        LaserDirection.HORIZONTAL -> {
            for (col in 0 until width) {
                world.findEntityAt(origin.row, col)?.let(targets::add)
            }
        }
        LaserDirection.VERTICAL -> {
            for (row in 0 until height) {
                world.findEntityAt(row, origin.col)?.let(targets::add)
            }
        }
    }
    return targets
}

internal fun resolveAreaOfEffect(
    world: World,
    targets: Set<Int>,
    pieceSet: PieceSet = PieceSetLoader.default(),
): Set<Int> {
    val iceCubeMapper: ComponentMapper<IceCubeComponent> = world.mapper()
    val hitByAreaEffectMapper: ComponentMapper<HitByAreaEffectComponent> = world.mapper()
    val activatedLaserMapper: ComponentMapper<ActivatedLaserComponent> = world.mapper()
    val fusedBombMapper: ComponentMapper<FusedBombComponent> = world.mapper()
    val activatedSwirlMapper: ComponentMapper<ActivatedSwirlComponent> = world.mapper()
    val consumedMapper: ComponentMapper<ConsumedComponent> = world.mapper()
    val spawnPointMapper: ComponentMapper<SpawnPointComponent> = world.mapper()

    val iceInRange = targets.filter {
        iceCubeMapper.has(it) && !consumedMapper.has(it)
    }.toSet()
    iceInRange.forEach { hitByAreaEffectMapper.set(it, HitByAreaEffectComponent()) }

    // Chain reactions: an entity caught in this area-of-effect activates if its
    // piece definition declares CaughtInAreaEffect — the effect kind drives the
    // engine's response (laser sweeps, bomb fuses, swirl arms).
    fun catchableEffect(entityId: Int): EffectKind? {
        if (!world.hasActivation(entityId, pieceSet, Activation.CAUGHT_IN_AREA_EFFECT)) return null
        return world.onActivateOf(entityId, pieceSet)?.effect
    }

    targets.filter { entityId ->
        catchableEffect(entityId) == EffectKind.LINE_CLEAR &&
            !consumedMapper.has(entityId) &&
            !activatedLaserMapper.has(entityId)
    }.forEach { entityId ->
        activateLaser(world, entityId, pieceSet, activatedLaserMapper)
    }

    targets.filter { entityId ->
        val effect = catchableEffect(entityId)
        (effect == EffectKind.AREA_BLAST_3X3 || effect == EffectKind.COLLECT_COLOUR) &&
            !consumedMapper.has(entityId) &&
            !fusedBombMapper.has(entityId)
    }.forEach { entityId ->
        fusedBombMapper.set(entityId, FusedBombComponent())
        if (catchableEffect(entityId) == EffectKind.COLLECT_COLOUR &&
            !activatedSwirlMapper.has(entityId)) {
            activatedSwirlMapper.set(
                entityId,
                ActivatedSwirlComponent(targetPieceId = world.mostCommonJellyPieceId(pieceSet) ?: PieceIds.EMBER_SEED),
            )
        }
    }

    val retained = targets.filter { entityId ->
        (fusedBombMapper.has(entityId) || activatedLaserMapper.has(entityId)) &&
            !consumedMapper.has(entityId)
    }.toSet()
    val spawns = targets.filter { spawnPointMapper.has(it) }.toSet()

    return targets - iceInRange - retained - spawns
}

internal fun activateLaser(
    world: World,
    entityId: Int,
    pieceSet: PieceSet,
    activatedLaserMapper: ComponentMapper<ActivatedLaserComponent>,
    directionsOverride: Set<LaserDirection>? = null,
    forcedConsumedEntities: Set<Int> = emptySet(),
) {
    val defaultDir = world.laserDirectionOf(entityId, pieceSet) ?: return
    val current = activatedLaserMapper[entityId]
    val directions = directionsOverride ?: setOf(defaultDir)
    activatedLaserMapper.set(
        entityId,
        ActivatedLaserComponent(
            directions = current?.directions?.plus(directions) ?: directions,
            forcedConsumedEntities = current?.forcedConsumedEntities?.plus(forcedConsumedEntities)
                ?: forcedConsumedEntities,
        ),
    )
}
